import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { DaySchedule, ScheduleEntry, WorkPosition } from '../types/workSchedule';

const CURRENT_COLLECTION = 'calendar-calendar';
const ARCHIVE_COLLECTION = 'calendar-calendar-reseted';

const retryOperation = async <T>(operation: () => Promise<T>, maxRetries = 3): Promise<T> => {
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 400));
    }
  }
  throw new Error('Max retries exceeded');
};

const toDate = (value: unknown): Date => {
  if (value instanceof Date) {
    return value;
  }

  if (value instanceof Timestamp) {
    return value.toDate();
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return new Date(value);
  }

  return new Date();
};

const toPosition = (value: unknown): WorkPosition => {
  if (value === 'table' || value === 'bar' || value === 'security' || value === 'none') {
    return value;
  }
  return 'none';
};

const createEmptyDaySchedule = (): DaySchedule => ({
  morning: false,
  afternoon: false,
  morningPosition: 'none',
  afternoonPosition: 'none',
});

const ensurePositionFields = (rawSchedule: Record<number, Partial<DaySchedule>>): Record<number, DaySchedule> => {
  const normalized: Record<number, DaySchedule> = {};

  Object.keys(rawSchedule).forEach((key) => {
    const numericKey = Number(key);
    const dayData = rawSchedule[numericKey] ?? {};

    normalized[numericKey] = {
      morning: Boolean(dayData.morning),
      afternoon: Boolean(dayData.afternoon),
      morningPosition: toPosition(dayData.morningPosition),
      afternoonPosition: toPosition(dayData.afternoonPosition),
      morningStoreLocation:
        typeof dayData.morningStoreLocation === 'string' ? dayData.morningStoreLocation : undefined,
      afternoonStoreLocation:
        typeof dayData.afternoonStoreLocation === 'string' ? dayData.afternoonStoreLocation : undefined,
    };
  });

  return normalized;
};

const normalizeWeekStart = (input: Date): Date => {
  const date = new Date(input);
  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diffToMonday);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getWeekRange = (weekOf: Date): { start: Date; end: Date } => {
  const start = normalizeWeekStart(weekOf);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return { start, end };
};

const mapSchedule = (id: string, data: Record<string, unknown>): ScheduleEntry => ({
  id,
  userId: String(data.userId ?? ''),
  userName: String(data.userName ?? ''),
  userAvatar: String(data.userAvatar ?? ''),
  weekOf: normalizeWeekStart(toDate(data.weekOf)),
  schedule: ensurePositionFields((data.schedule as Record<number, Partial<DaySchedule>>) ?? {}),
  createdAt: toDate(data.createdAt),
  updatedAt: toDate(data.updatedAt),
  archivedAt: data.archivedAt ? toDate(data.archivedAt) : undefined,
  resetBy: typeof data.resetBy === 'string' ? data.resetBy : undefined,
});

const sortByNewest = (a: ScheduleEntry, b: ScheduleEntry): number => {
  return (b.updatedAt?.getTime() ?? 0) - (a.updatedAt?.getTime() ?? 0);
};

const cleanSchedulePayload = (schedule: Record<number, DaySchedule>): Record<number, DaySchedule> => {
  const cleaned: Record<number, DaySchedule> = {};

  Object.keys(schedule).forEach((key) => {
    const dayIndex = Number(key);
    const day = schedule[dayIndex];

    if (!day) {
      return;
    }

    const sanitized: DaySchedule = {
      morning: day.morning,
      afternoon: day.afternoon,
      morningPosition: day.morning ? day.morningPosition : 'none',
      afternoonPosition: day.afternoon ? day.afternoonPosition : 'none',
      morningStoreLocation: day.morning ? day.morningStoreLocation : undefined,
      afternoonStoreLocation: day.afternoon ? day.afternoonStoreLocation : undefined,
    };

    if (sanitized.morning || sanitized.afternoon) {
      cleaned[dayIndex] = sanitized;
    }
  });

  return cleaned;
};

export const workScheduleService = {
  normalizeWeekStart,

  async getSchedulesInRange(startDate: Date, endDate: Date): Promise<ScheduleEntry[]> {
    return retryOperation(async () => {
      const normalizedStart = normalizeWeekStart(startDate);
      const normalizedEnd = normalizeWeekStart(endDate);
      if (normalizedEnd.getTime() < endDate.getTime()) {
        normalizedEnd.setDate(normalizedEnd.getDate() + 7);
      }

      const currentQuery = query(
        collection(db, CURRENT_COLLECTION),
        where('weekOf', '>=', normalizedStart),
        where('weekOf', '<', normalizedEnd)
      );
      const archivedQuery = query(
        collection(db, ARCHIVE_COLLECTION),
        where('weekOf', '>=', normalizedStart),
        where('weekOf', '<', normalizedEnd)
      );

      const [currentSnapshot, archivedSnapshot] = await Promise.all([getDocs(currentQuery), getDocs(archivedQuery)]);

      const mappedCurrent = currentSnapshot.docs.map((item) => mapSchedule(item.id, item.data() as Record<string, unknown>));
      const mappedArchived = archivedSnapshot.docs.map((item) => mapSchedule(item.id, item.data() as Record<string, unknown>));

      const mergedMap = new Map<string, ScheduleEntry>();
      [...mappedArchived, ...mappedCurrent].forEach((item) => {
        const key = `${item.userId}-${item.weekOf.getTime()}`;
        const existing = mergedMap.get(key);
        if (!existing || (item.updatedAt?.getTime() ?? 0) > (existing.updatedAt?.getTime() ?? 0)) {
          mergedMap.set(key, item);
        }
      });

      return Array.from(mergedMap.values()).sort(sortByNewest);
    });
  },

  async addSchedule(payload: Omit<ScheduleEntry, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return retryOperation(async () => {
      const now = new Date();
      const weekOf = normalizeWeekStart(payload.weekOf);
      const docRef = await addDoc(collection(db, CURRENT_COLLECTION), {
        ...payload,
        weekOf,
        schedule: cleanSchedulePayload(payload.schedule),
        createdAt: now,
        updatedAt: now,
      });

      return docRef.id;
    });
  },

  async updateSchedule(id: string, payload: Partial<ScheduleEntry>): Promise<void> {
    return retryOperation(async () => {
      await updateDoc(doc(db, CURRENT_COLLECTION, id), {
        ...payload,
        weekOf: payload.weekOf ? normalizeWeekStart(payload.weekOf) : undefined,
        schedule: payload.schedule ? cleanSchedulePayload(payload.schedule) : undefined,
        updatedAt: new Date(),
      });
    });
  },

  async getScheduleByUserAndWeek(userId: string, weekOf: Date): Promise<ScheduleEntry | null> {
    return retryOperation(async () => {
      const { start, end } = getWeekRange(weekOf);
      const weekQuery = query(
        collection(db, CURRENT_COLLECTION),
        where('userId', '==', userId),
        where('weekOf', '>=', start),
        where('weekOf', '<', end)
      );

      const snapshot = await getDocs(weekQuery);
      if (snapshot.empty) {
        return null;
      }

      const schedules = snapshot.docs
        .map((item) => mapSchedule(item.id, item.data() as Record<string, unknown>))
        .sort(sortByNewest);

      return schedules[0] ?? null;
    });
  },

  async upsertSchedule(payload: Omit<ScheduleEntry, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const existing = await this.getScheduleByUserAndWeek(payload.userId, payload.weekOf);
    if (existing?.id) {
      await this.updateSchedule(existing.id, payload);
      return existing.id;
    }
    return this.addSchedule(payload);
  },

  async getAllSchedulesForWeek(weekOf: Date): Promise<ScheduleEntry[]> {
    return retryOperation(async () => {
      const { start, end } = getWeekRange(weekOf);
      const weekQuery = query(
        collection(db, CURRENT_COLLECTION),
        where('weekOf', '>=', start),
        where('weekOf', '<', end)
      );

      const snapshot = await getDocs(weekQuery);
      return snapshot.docs
        .map((item) => mapSchedule(item.id, item.data() as Record<string, unknown>))
        .sort(sortByNewest);
    });
  },

  async getArchivedSchedulesForWeek(weekOf: Date): Promise<ScheduleEntry[]> {
    return retryOperation(async () => {
      const { start, end } = getWeekRange(weekOf);
      const weekQuery = query(
        collection(db, ARCHIVE_COLLECTION),
        where('weekOf', '>=', start),
        where('weekOf', '<', end)
      );

      const snapshot = await getDocs(weekQuery);
      return snapshot.docs
        .map((item) => mapSchedule(item.id, item.data() as Record<string, unknown>))
        .sort(sortByNewest);
    });
  },

  async getAllSchedulesByUser(userId: string): Promise<ScheduleEntry[]> {
    return retryOperation(async () => {
      const schedulesQuery = query(collection(db, CURRENT_COLLECTION), where('userId', '==', userId));
      const snapshot = await getDocs(schedulesQuery);
      return snapshot.docs
        .map((item) => mapSchedule(item.id, item.data() as Record<string, unknown>))
        .sort(sortByNewest);
    });
  },

  async deleteSchedulesByUserId(userId: string): Promise<void> {
    return retryOperation(async () => {
      const currentQuery = query(collection(db, CURRENT_COLLECTION), where('userId', '==', userId));
      const archiveQuery = query(collection(db, ARCHIVE_COLLECTION), where('userId', '==', userId));

      const [currentSnapshot, archiveSnapshot] = await Promise.all([getDocs(currentQuery), getDocs(archiveQuery)]);
      const batch = writeBatch(db);

      currentSnapshot.docs.forEach((item) => batch.delete(item.ref));
      archiveSnapshot.docs.forEach((item) => batch.delete(item.ref));

      await batch.commit();
    });
  },

  async cleanupDuplicateSchedulesForWeek(weekOf: Date): Promise<void> {
    return retryOperation(async () => {
      const schedules = await this.getAllSchedulesForWeek(weekOf);
      const duplicateMap = new Map<string, ScheduleEntry[]>();

      schedules.forEach((item) => {
        const key = `${item.userId}-${item.weekOf.getTime()}`;
        const current = duplicateMap.get(key) ?? [];
        current.push(item);
        duplicateMap.set(key, current);
      });

      const duplicatesToDelete: string[] = [];

      duplicateMap.forEach((items) => {
        if (items.length <= 1) {
          return;
        }

        items.sort(sortByNewest);
        items.slice(1).forEach((item) => {
          if (item.id) {
            duplicatesToDelete.push(item.id);
          }
        });
      });

      await Promise.all(duplicatesToDelete.map((id) => deleteDoc(doc(db, CURRENT_COLLECTION, id))));
    });
  },

  async resetAllSchedules(resetBy = 'system'): Promise<void> {
    return retryOperation(async () => {
      const snapshot = await getDocs(collection(db, CURRENT_COLLECTION));
      if (snapshot.empty) {
        return;
      }

      const batch = writeBatch(db);
      const archiveAt = new Date();

      snapshot.docs.forEach((item) => {
        const data = item.data();
        const archiveRef = doc(collection(db, ARCHIVE_COLLECTION));

        batch.set(archiveRef, {
          ...data,
          originalScheduleId: item.id,
          archivedAt: archiveAt,
          resetBy,
        });
        batch.delete(item.ref);
      });

      await batch.commit();
    });
  },
};
